import { useCallback, useEffect, useState } from 'react'

/**
 * How SEC filings are painted, everywhere a filing is read (the news
 * sidebar's Document tab and the Research Filings viewer):
 *   theme     the app's background and text colors (dark mode reads as dark)
 *   original  the filing's own colors on a white page, as EDGAR shows it
 *   paper     the filing's colors on a warm page
 */
export type FilingViewerMode = 'theme' | 'original' | 'paper'

const STORAGE_KEY = 'edge.filing-viewer.mode'
const MODES: FilingViewerMode[] = ['theme', 'original', 'paper']
/** Same-tab sync: every mounted viewer follows a change made in any of them. */
const CHANGE_EVENT = 'edge:filing-viewer-mode'

function read(): FilingViewerMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'original' || stored === 'paper' || stored === 'theme') return stored
    // Legacy "white" (and anything unknown) reads as the app theme: the old
    // hook stored its default on mount, so "white" is rarely a real choice.
  } catch {
    // ignore
  }
  return 'theme'
}

export function useFilingViewerMode() {
  const [mode, setModeState] = useState<FilingViewerMode>(read)

  useEffect(() => {
    const sync = () => setModeState(read())
    // Catch a change made between this render and this subscription (the
    // profile sync can apply the saved mode in the same commit).
    sync()
    window.addEventListener(CHANGE_EVENT, sync)
    // Other tabs change localStorage and fire `storage` here.
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setMode = useCallback((next: FilingViewerMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore
    }
    setModeState(next)
    window.dispatchEvent(new Event(CHANGE_EVENT))
  }, [])

  const cycleMode = useCallback(() => {
    setMode(MODES[(MODES.indexOf(read()) + 1) % MODES.length]!)
  }, [setMode])

  return { mode, setMode, cycleMode }
}
